import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Close,
  Send,
  Description,
  Email as EmailIcon,
  Build,
} from '@mui/icons-material';

interface DocumentType {
  id: number;
  filename?: string;
  original_name?: string;
  document_name?: string;
  description?: string;
  mime_type?: string;
  source_table?: string;
  photo_type?: string;
  file_path?: string;
}

interface EmailDocumentModalProps {
  open: boolean;
  onClose: () => void;
  document?: DocumentType | null;
  documents?: DocumentType[];
  availableItems?: DocumentType[];
  customerEmail?: string;
  customerName?: string;
  customerAddress?: string;
  projectId?: string | number;
  onSuccess?: () => void;
}

const EmailDocumentModal: React.FC<EmailDocumentModalProps> = ({
  open,
  onClose,
  document,
  documents: documentsProp,
  availableItems = [],
  customerEmail = '',
  customerName = '',
  customerAddress = '',
  projectId,
  onSuccess,
}) => {
  // Initial documents from props
  const initialDocs = useMemo(() => {
    return documentsProp || (document ? [document] : []);
  }, [documentsProp, document]);

  // Track if form has been initialized for this modal session
  const formInitialized = useRef(false);

  // State for selected documents (can be modified by adding more)
  const [selectedDocs, setSelectedDocs] = useState<DocumentType[]>([]);
  const [showAddMore, setShowAddMore] = useState(false);

  const [emailForm, setEmailForm] = useState({
    to: '',
    cc: '',
    subject: '',
    message: `Hi ${customerName || 'there'},\n\nPlease find attached your document from Gordons Makeovers.\n\nThanks,\nThe Gordons Team`,
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [fitters, setFitters] = useState<{id: number; name: string; email: string}[]>([]);
  const [selectedFitterIds, setSelectedFitterIds] = useState<number[]>([]);
  const [fitterCcEmails, setFitterCcEmails] = useState<string[]>([]);

  // Initialize selectedDocs when modal opens
  useEffect(() => {
    if (open && initialDocs.length > 0) {
      setSelectedDocs(initialDocs);
      setShowAddMore(false);
    }
    if (!open) {
      setSelectedDocs([]);
      setShowAddMore(false);
    }
  }, [open, initialDocs]);

  // Fetch CC emails, fitter emails, and fitter CC emails when modal opens
  useEffect(() => {
    if (open) {
      fetchCcEmails();
      fetchFitters();
      fetchFitterCcEmails();
      setSelectedFitterIds([]);
    }
  }, [open]);

  const fetchCcEmails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/settings/supplier_cc_emails', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.value) {
        try {
          const emails = JSON.parse(data.value);
          setCcEmails(emails);
        } catch (e) {
          setCcEmails([]);
        }
      }
    } catch (error) {
      console.error('Error fetching CC emails:', error);
    }
  };

  const fetchFitters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/fitters/active', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        setFitters(data.filter((f: any) => f.email).map((f: any) => ({ id: f.id, name: f.name, email: f.email })));
      }
    } catch (error) {
      console.error('Error fetching fitters:', error);
      setFitters([]);
    }
  };

  const fetchFitterCcEmails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/settings/fitter_cc_emails', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.value) {
        try {
          const emails = JSON.parse(data.value);
          setFitterCcEmails(emails);
        } catch (e) {
          setFitterCcEmails([]);
        }
      }
    } catch (error) {
      console.error('Error fetching fitter CC emails:', error);
      setFitterCcEmails([]);
    }
  };

  // Update form when modal opens with new document(s) - only initialize once per modal open
  useEffect(() => {
    if (open && initialDocs.length > 0 && !formInitialized.current) {
      formInitialized.current = true;
      const docNames = initialDocs.map(d => d.original_name || d.filename || d.document_name || 'Document');
      const subject = initialDocs.length === 1 ? docNames[0] : `${initialDocs.length} Documents from Gordons Makeovers`;
      setEmailForm({
        to: '',
        cc: '',
        subject: subject,
        message: `Hi ${customerName || 'there'},\n\nPlease find attached your document${initialDocs.length > 1 ? 's' : ''} from Gordons Makeovers.\n\nThanks,\nThe Gordons Team`,
      });
      setError(null);
      setSuccess(false);
    }
    // Reset the flag when modal closes
    if (!open) {
      formInitialized.current = false;
    }
  }, [initialDocs, open, customerEmail, customerName]);

  const handleSendToFitters = () => {
    if (selectedFitterIds.length === 0) {
      setError('Please select at least one fitter.');
      return;
    }

    const selectedEmails = fitters
      .filter(f => selectedFitterIds.includes(f.id))
      .map(f => f.email);

    const fitterMessage = `Hi,\n\nPlease find attached documents for the ${customerName || 'customer'} job${customerAddress ? ` at ${customerAddress}` : ''}.\n\nThanks,\nThe Gordons Team`;

    setEmailForm({
      ...emailForm,
      to: selectedEmails.join('; '),
      cc: fitterCcEmails.join('; '),
      subject: `Job Documents - ${customerName || 'Customer'}`,
      message: fitterMessage,
    });
  };

  const handleToggleFitter = (id: number) => {
    setSelectedFitterIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAllFitters = () => {
    if (selectedFitterIds.length === fitters.length) {
      setSelectedFitterIds([]);
    } else {
      setSelectedFitterIds(fitters.map(f => f.id));
    }
  };

  const handleSend = async () => {
    if (selectedDocs.length === 0) return;

    // Check for unsupported file formats
    for (const doc of selectedDocs) {
      const filename = doc.document_name || doc.original_name || doc.filename || doc.file_path || '';
      if (filename.toLowerCase().endsWith('.webp')) {
        setError('Sorry, cannot send WebP files. Please remove them from the selection.');
        return;
      }
    }

    // Validate emails (support multiple semicolon-separated)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const toEmails = emailForm.to.split(/[;,]/).map(e => e.trim()).filter(e => e);

    for (const email of toEmails) {
      if (!emailRegex.test(email)) {
        setError(`Invalid email address: ${email}`);
        return;
      }
    }

    if (toEmails.length === 0) {
      setError('Please enter at least one email address');
      return;
    }

    // Validate CC emails if provided
    const ccEmailList = emailForm.cc ? emailForm.cc.split(/[;,]/).map(e => e.trim()).filter(e => e) : [];
    for (const email of ccEmailList) {
      if (!emailRegex.test(email)) {
        setError(`Invalid CC email address: ${email}`);
        return;
      }
    }

    if (!emailForm.subject.trim()) {
      setError('Please enter a subject');
      return;
    }

    setSending(true);
    setError(null);

    try {
      // Use multi-document endpoint if multiple docs, multiple recipients, or if we have CC emails
      if (selectedDocs.length > 1 || ccEmailList.length > 0 || toEmails.length > 1) {
        // Use new multi-document endpoint
        const response = await fetch('/api/email/send-documents', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            to: toEmails,
            cc: ccEmailList,
            subject: emailForm.subject,
            message: emailForm.message,
            documents: selectedDocs.map(doc => ({
              id: doc.id,
              source_table: doc.source_table || (doc.photo_type ? 'project_photos' : 'documents'),
              photo_type: doc.photo_type,
            })),
            projectId: projectId,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to send email');
        }
      } else {
        // Single document without CC - use existing endpoint for backward compatibility
        const doc = selectedDocs[0];
        let apiEndpoint = '';

        if (doc.photo_type) {
          if (!projectId) {
            throw new Error('Project ID is required for photo emails');
          }
          apiEndpoint = `/api/project-photos/${projectId}/photos/${doc.id}/email`;
        } else if (doc.source_table === 'project_documents' || (!doc.source_table && projectId)) {
          if (!projectId) {
            throw new Error('Project ID is required for project document emails');
          }
          apiEndpoint = `/api/projects/${projectId}/documents/${doc.id}/email`;
        } else {
          apiEndpoint = `/api/documents/${doc.id}/email`;
        }

        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            to: toEmails[0],
            subject: emailForm.subject,
            message: emailForm.message,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to send email');
        }
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
        if (onSuccess) onSuccess();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (!sending) {
      setEmailForm({
        to: '',
        cc: '',
        subject: '',
        message: `Hi ${customerName || 'there'},\n\nPlease find attached your document from Gordons Makeovers.\n\nThanks,\nThe Gordons Team`,
      });
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  // Get items not already selected
  const unselectedItems = useMemo(() => {
    const selectedIds = new Set(selectedDocs.map(d => `${d.id}-${d.source_table || d.photo_type || ''}`));
    return availableItems.filter(item => {
      const itemKey = `${item.id}-${item.source_table || item.photo_type || ''}`;
      return !selectedIds.has(itemKey);
    });
  }, [availableItems, selectedDocs]);

  const handleAddDocument = (doc: DocumentType) => {
    setSelectedDocs(prev => [...prev, doc]);
  };

  const handleRemoveDocument = (idx: number) => {
    setSelectedDocs(prev => prev.filter((_, i) => i !== idx));
  };

  if (selectedDocs.length === 0 && initialDocs.length === 0) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon color="primary" />
          <Typography variant="h6">Email Document{selectedDocs.length > 1 ? 's' : ''}</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={sending}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Email sent successfully!
          </Alert>
        ) : (
          <>
            {/* Document Info */}
            <Box sx={{
              mb: 2,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Description color="action" />
                <Typography variant="subtitle2" color="text.secondary">
                  {selectedDocs.length === 1 ? 'Document to Send' : `${selectedDocs.length} Documents to Send`}
                </Typography>
              </Box>
              {selectedDocs.length === 1 ? (
                <>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedDocs[0].document_name || selectedDocs[0].original_name || selectedDocs[0].filename || 'Document'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedDocs[0].photo_type ? 'Photo' :
                     selectedDocs[0].mime_type === 'application/pdf' ? 'PDF Document' :
                     selectedDocs[0].mime_type?.startsWith('image/') ? 'Image File' : 'Document'}
                  </Typography>
                </>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {selectedDocs.map((doc, idx) => (
                    <Chip
                      key={idx}
                      size="small"
                      label={doc.document_name || doc.original_name || doc.filename || 'Document'}
                      variant="outlined"
                      onDelete={() => handleRemoveDocument(idx)}
                    />
                  ))}
                </Box>
              )}

              {/* Add More Button */}
              {unselectedItems.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowAddMore(!showAddMore)}
                    disabled={sending}
                  >
                    {showAddMore ? '- Hide' : `+ Add More (${unselectedItems.length} available)`}
                  </Button>
                  {showAddMore && (
                    <Box sx={{
                      mt: 1,
                      maxHeight: 200,
                      overflowY: 'auto',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: 1
                    }}>
                      {unselectedItems.map((item, idx) => {
                        const isImage = item.photo_type ||
                          item.mime_type?.startsWith('image/') ||
                          item.filename?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i) ||
                          item.file_path?.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/i);

                        // Build image URL
                        let imageUrl = '';
                        if (isImage) {
                          if (item.source_table === 'documents') {
                            imageUrl = `/api/documents/view/${item.id}`;
                          } else if (item.source_table === 'project_photos' || item.photo_type) {
                            imageUrl = `/api/project-photos/uploads/${item.file_path}`;
                          } else {
                            imageUrl = `/uploads/${item.file_path}`;
                          }
                        }

                        return (
                          <Box
                            key={idx}
                            onClick={() => handleAddDocument(item)}
                            sx={{
                              cursor: 'pointer',
                              border: '2px solid',
                              borderColor: 'grey.300',
                              borderRadius: 1,
                              overflow: 'hidden',
                              '&:hover': {
                                borderColor: 'primary.main',
                                boxShadow: 2
                              }
                            }}
                          >
                            {isImage ? (
                              <Box
                                component="img"
                                src={imageUrl}
                                alt={item.filename || 'Image'}
                                sx={{
                                  width: '100%',
                                  height: 60,
                                  objectFit: 'cover'
                                }}
                                onError={(e: any) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <Box sx={{
                                height: 60,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'grey.100'
                              }}>
                                <Description color="action" />
                              </Box>
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                p: 0.5,
                                textAlign: 'center',
                                fontSize: '0.65rem',
                                wordBreak: 'break-word',
                                lineHeight: 1.2
                              }}
                            >
                              {(item.description && !item.description.match(/^Uploaded \d+ photo/)) ? item.description : (item.document_name || item.original_name || item.filename || 'Document')}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* Send to Fitters Section */}
            {fitters.length > 0 && (
              <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Build fontSize="small" color="secondary" />
                  <Typography variant="subtitle2">Send to Fitters (click to select)</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                  <Chip
                    label="Select All"
                    variant={selectedFitterIds.length === fitters.length ? 'filled' : 'outlined'}
                    color={selectedFitterIds.length === fitters.length ? 'secondary' : 'default'}
                    onClick={handleSelectAllFitters}
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                  {fitters.map(f => (
                    <Chip
                      key={f.id}
                      label={f.name}
                      variant={selectedFitterIds.includes(f.id) ? 'filled' : 'outlined'}
                      color={selectedFitterIds.includes(f.id) ? 'primary' : 'default'}
                      onClick={() => handleToggleFitter(f.id)}
                      size="small"
                    />
                  ))}
                </Box>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  startIcon={<Build />}
                  onClick={handleSendToFitters}
                  disabled={sending || selectedFitterIds.length === 0}
                >
                  Use Selected Fitters
                </Button>
              </Box>
            )}

            {/* Email Form */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Email To"
                value={emailForm.to}
                onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                fullWidth
                required
                error={!!error && error.includes('email') && !error.includes('CC')}
                disabled={sending}
                helperText="Separate multiple emails with semicolon (;)"
                InputProps={{
                  startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />,
                }}
              />

              <TextField
                label="CC"
                value={emailForm.cc}
                onChange={(e) => setEmailForm({ ...emailForm, cc: e.target.value })}
                fullWidth
                disabled={sending}
                helperText="Separate multiple emails with semicolon (;)"
              />

              <TextField
                label="Subject"
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                fullWidth
                required
                disabled={sending}
              />

              <TextField
                label="Message"
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                fullWidth
                multiline
                rows={6}
                disabled={sending}
              />

              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={sending}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          disabled={sending || success}
          variant="contained"
          startIcon={sending ? <CircularProgress size={20} /> : <Send />}
        >
          {sending ? 'Sending...' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailDocumentModal;
